import { SUPPORTED_LOCALES, type SupportedLocale } from '@fleetcore/shared'
import { Check, ChevronDown, KeyRound, LogOut } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuth } from '../auth/use-auth'
import { currentLocale, setLocale } from '../i18n'
import { cn } from '../lib/cn'
import { MODE_ICONS } from '../theme/theme-toggle'
import { THEME_MODES, useThemeStore } from '../theme/theme-store'
import { ChangePasswordDialog } from './change-password-dialog'
import { Menu, MenuItem, MenuSeparator } from './ui/menu'

/** Two letters from the display name, or the first of the email as a fallback. */
function initialsOf(displayName: string, email: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
  const single = parts[0] ?? email
  return single.slice(0, 2).toUpperCase()
}

export function UserMenu() {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const [changingPassword, setChangingPassword] = useState(false)

  if (!user) return null

  const label = user.displayName || user.email
  const roleLabels = user.roles.map((role) => t(`common:roles.${role}`)).join(', ')

  return (
    <>
      <Menu
        label={t('common:userMenu.open')}
        trigger={({ open }) => (
          <span className="flex h-9 items-center gap-2 rounded-md ps-1 pe-1.5 transition-colors hover:bg-surface-hover">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-subtle text-xs font-semibold text-accent-text">
              {initialsOf(user.displayName, user.email)}
            </span>
            <span className="hidden max-w-[10rem] truncate text-sm text-foreground sm:block">
              {label}
            </span>
            <ChevronDown
              aria-hidden
              className={cn(
                'h-3.5 w-3.5 text-foreground-subtle transition-transform',
                open && 'rotate-180',
              )}
            />
          </span>
        )}
      >
        {(close) => (
          <>
            <div className="px-2 py-1.5">
              <p className="text-xs text-foreground-subtle">{t('common:userMenu.signedInAs')}</p>
              <p className="truncate text-sm font-medium text-foreground">{label}</p>
              {user.email && user.email !== label ? (
                <p className="truncate text-xs text-foreground-muted">{user.email}</p>
              ) : null}
              <p className="mt-0.5 truncate text-xs text-foreground-subtle">{roleLabels}</p>
            </div>

            <MenuSeparator />

            {/* Phones only: the top bar hides its own language and theme buttons below
              `sm` to give the page title room, so the choices live here instead. */}
            <div className="sm:hidden">
              <PhonePreferences close={close} />
              <MenuSeparator />
            </div>

            <MenuItem
              onSelect={() => {
                close()
                setChangingPassword(true)
              }}
            >
              <KeyRound aria-hidden className="h-3.5 w-3.5" />
              {t('common:userMenu.changePassword')}
            </MenuItem>

            <MenuItem
              onSelect={() => {
                close()
                void logout()
              }}
            >
              <LogOut aria-hidden className="h-3.5 w-3.5" />
              {t('common:userMenu.logout')}
            </MenuItem>
          </>
        )}
      </Menu>
      {changingPassword ? (
        <ChangePasswordDialog onClose={() => setChangingPassword(false)} />
      ) : null}
    </>
  )
}

function PhonePreferences({ close }: { close: () => void }) {
  const { t, i18n } = useTranslation()
  void i18n.language // re-render on switch
  const activeLocale = currentLocale()
  const mode = useThemeStore((state) => state.mode)
  const setMode = useThemeStore((state) => state.setMode)

  return (
    <>
      <p className="px-2 pb-0.5 pt-1.5 text-xs text-foreground-subtle">
        {t('common:language.label')}
      </p>
      {SUPPORTED_LOCALES.map((locale: SupportedLocale) => (
        <MenuItem
          key={locale}
          selected={locale === activeLocale}
          onSelect={() => {
            void setLocale(locale)
            close()
          }}
        >
          <Check
            aria-hidden
            className={locale === activeLocale ? 'h-3.5 w-3.5' : 'h-3.5 w-3.5 opacity-0'}
          />
          {t(`common:language.${locale}`)}
        </MenuItem>
      ))}
      <p className="px-2 pb-0.5 pt-1.5 text-xs text-foreground-subtle">{t('common:theme.label')}</p>
      {THEME_MODES.map((candidate) => {
        const Icon = MODE_ICONS[candidate]
        return (
          <MenuItem
            key={candidate}
            selected={candidate === mode}
            onSelect={() => {
              setMode(candidate)
              close()
            }}
          >
            <Icon aria-hidden className="h-3.5 w-3.5" />
            <span className="flex-1">{t(`common:theme.${candidate}`)}</span>
            {candidate === mode ? <Check aria-hidden className="h-3.5 w-3.5" /> : null}
          </MenuItem>
        )
      })}
    </>
  )
}
